import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EventAttendee, EventAttendeeList, EventAttendeesBulkResult, Group, SiteEvent } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { ExportButton } from '../../components/ExportButton';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, EmptyRow, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { useFullSites } from './api';
import { parseAttendees } from './attendeeCsv';
import { EVENT_TONE, EventForm, eventsKey } from './EventsPage';

const GroupNames = z.array(Group);
const PAGE_SIZE = 25;
const ATTENDEE_STATUSES = ['expected', 'arrived', 'no_show', 'cancelled'] as const;
const ATTENDEE_TONE: Record<EventAttendee['status'], 'good' | 'warn' | 'neutral' | 'bad'> = { expected: 'neutral', arrived: 'good', no_show: 'bad', cancelled: 'warn' };

function AddOne({ eventId, onDone }: { eventId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const add = useMutation({
    mutationFn: () => api.post(`/events-mgmt/events/${eventId}/attendees`, { body: { name, email: email.trim() === '' ? null : email, company, memberId: null }, schema: EventAttendee }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: eventsKey });
      onDone();
    },
  });
  return (
    <form
      className="grid gap-3 sm:grid-cols-3"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      <div className="sm:col-span-3">
        <ErrorLine error={add.error} />
      </div>
      <TextField label={t('siteF.events.attendee.name')} required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
      <TextField label={t('siteF.events.attendee.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <TextField label={t('siteF.events.attendee.company')} maxLength={120} value={company} onChange={(e) => setCompany(e.target.value)} />
      <div className="flex gap-2 sm:col-span-3">
        <Button type="submit" variant="primary" disabled={add.isPending || name.trim() === ''}>
          {t('siteF.events.attendee.add')}
        </Button>
        <Button onClick={onDone}>{t('common.cancel')}</Button>
      </div>
    </form>
  );
}

function BulkAdd({ eventId, onDone }: { eventId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [preRegister, setPreRegister] = useState(false);
  const parsed = parseAttendees(text);
  const bulk = useMutation({
    mutationFn: () => api.post(`/events-mgmt/events/${eventId}/attendees/bulk`, { body: { attendees: parsed.attendees, preRegister }, schema: EventAttendeesBulkResult }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: eventsKey });
    },
  });
  const readFile = async (file: File | undefined) => {
    if (file) setText(await file.text());
  };
  return (
    <div className="space-y-3">
      <ErrorLine error={bulk.error} />
      <p className="text-sm text-slate-600">{t('siteF.events.bulk.hint')}</p>
      <div>
        <label htmlFor="attendee-file" className="mb-1 block text-sm font-medium text-slate-800">
          {t('siteF.events.bulk.file')}
        </label>
        <input id="attendee-file" type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => void readFile(e.target.files?.[0])} className="block text-sm" />
      </div>
      <div>
        <label htmlFor="attendee-paste" className="mb-1 block text-sm font-medium text-slate-800">
          {t('siteF.events.bulk.paste')}
        </label>
        <textarea
          id="attendee-paste"
          rows={6}
          className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
          value={text}
          onChange={(e) => {
            bulk.reset();
            setText(e.target.value);
          }}
        />
      </div>
      <CheckboxField label={t('siteF.events.bulk.preRegister')} hint={t('siteF.events.bulk.preRegisterHint')} checked={preRegister} onChange={(e) => setPreRegister(e.target.checked)} />
      {text.trim() !== '' ? <p className="text-sm">{t('siteF.events.bulk.found', { n: parsed.attendees.length })}</p> : null}
      {parsed.rejected.length > 0 ? <Alert>{t('siteF.events.bulk.rejected', { lines: parsed.rejected.slice(0, 10).join(', ') })}</Alert> : null}
      {bulk.data ? <Alert kind="success">{t('siteF.events.bulk.done', { added: bulk.data.added, skipped: bulk.data.skippedDuplicates, pre: bulk.data.preRegistered })}</Alert> : null}
      <div className="flex gap-2">
        <Button variant="primary" disabled={bulk.isPending || parsed.attendees.length === 0} onClick={() => bulk.mutate()}>
          {t('siteF.events.bulk.submit')}
        </Button>
        <Button onClick={onDone}>{t('common.close')}</Button>
      </div>
    </div>
  );
}

function AttendeeRow({ attendee, canManage }: { attendee: EventAttendee; canManage: boolean }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: eventsKey });
  const setStatus = useMutation({
    mutationFn: (status: EventAttendee['status']) => api.patch(`/events-mgmt/attendees/${attendee.id}`, { body: { status }, schema: EventAttendee }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: () => api.delete(`/events-mgmt/attendees/${attendee.id}`), onSuccess: refresh });
  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="px-4 py-3 font-medium">
        {attendee.name}
        {attendee.walkIn ? (
          <span className="ml-2">
            <Badge tone="warn">{t('siteF.events.attendee.walkIn')}</Badge>
          </span>
        ) : null}
      </td>
      <td className="px-4 py-3">{attendee.company}</td>
      <td className="px-4 py-3">{attendee.email ?? ''}</td>
      <td className="px-4 py-3">
        <Badge tone={ATTENDEE_TONE[attendee.status]}>{t(`siteF.events.attendee.status.${attendee.status}`)}</Badge>
        {attendee.arrivedAt ? <span className="mt-1 block text-xs text-slate-600">{formatDateTime(attendee.arrivedAt)}</span> : null}
      </td>
      <td className="px-4 py-3">
        {canManage ? (
          <div className="flex flex-wrap items-center gap-2">
            <SelectField
              label={t('siteF.events.attendee.setStatus', { name: attendee.name })}
              value={attendee.status}
              disabled={setStatus.isPending || attendee.status === 'arrived'}
              onChange={(e) => setStatus.mutate(e.target.value as EventAttendee['status'])}
            >
              {ATTENDEE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`siteF.events.attendee.status.${s}`)}
                </option>
              ))}
            </SelectField>
            <ConfirmButton label={t('siteF.events.attendee.remove')} pending={remove.isPending} onConfirm={() => remove.mutate()} />
          </div>
        ) : null}
        <ErrorLine error={setStatus.error ?? remove.error} />
      </td>
    </tr>
  );
}

/** Portal page for one event: details, groups, live attendance, adding people (one, pasted or from a CSV) and export. */
export function EventPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useCan('events.manage');
  const canDelete = useCan('events.delete');
  const list = useListState();
  const [mode, setMode] = useState<'none' | 'edit' | 'one' | 'bulk'>('none');
  const sites = useFullSites();
  const groupsQ = useQuery({ queryKey: ['site-features', 'group-names'], queryFn: () => api.get('/groups', { query: { status: 'all' }, schema: GroupNames }) });
  const event = useQuery({
    queryKey: [...eventsKey, 'one', id],
    queryFn: () => api.get(`/events-mgmt/events/${id}`, { schema: SiteEvent }),
    // Attendance changes as people sign in.
    refetchInterval: 15_000,
  });
  const attendeeQuery = { search: list.search, page: list.page, pageSize: PAGE_SIZE };
  const attendees = useQuery({
    queryKey: [...eventsKey, 'attendees', id, attendeeQuery],
    queryFn: () => api.get(`/events-mgmt/events/${id}/attendees`, { query: attendeeQuery, schema: EventAttendeeList }),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });
  const setStatus = useMutation({
    mutationFn: (status: SiteEvent['status']) => api.patch(`/events-mgmt/events/${id}`, { body: { status }, schema: SiteEvent }),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventsKey }),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/events-mgmt/events/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: eventsKey });
      void navigate('/events');
    },
  });

  if (event.isPending) return <Spinner />;
  if (event.isError) return <ErrorPanel error={event.error} onRetry={() => void event.refetch()} />;
  const ev = event.data;
  const site = sites.data?.items.find((s) => s.id === ev.siteId);
  const zone = site?.timeZone ?? 'Europe/London';
  const groupNames = new Map((groupsQ.data ?? []).map((g) => [g.id, g.name]));
  const over = ev.capacity !== null && ev.counts.expected + ev.counts.walkIns > ev.capacity;
  const deletable = canDelete && (ev.status === 'draft' || ev.status === 'cancelled');

  return (
    <>
      <PageHeader
        title={ev.name}
        description={t('siteF.events.dates', { site: site?.name ?? '', start: formatDateTime(ev.startsAt, zone), end: formatDateTime(ev.endsAt, zone) })}
        actions={
          <>
            <ExportButton path={`/events-mgmt/events/${ev.id}/export`} fileName="event-attendees" />
            {canManage && mode !== 'edit' ? <Button onClick={() => setMode('edit')}>{t('common.edit')}</Button> : null}
          </>
        }
      />
      <ErrorLine error={setStatus.error ?? remove.error} />
      {mode === 'edit' ? <EventForm event={ev} defaultSiteId={ev.siteId} onDone={() => setMode('none')} /> : null}

      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <Card title={t('siteF.events.details')} className="lg:col-span-2">
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-600">{t('common.status')}</dt>
              <dd>
                <Badge tone={EVENT_TONE[ev.status]}>{t(`siteF.events.status.${ev.status}`)}</Badge>
              </dd>
            </div>
            <div>
              <dt className="text-slate-600">{t('siteF.events.groups')}</dt>
              <dd>{ev.groupIds.map((g) => groupNames.get(g) ?? '').filter(Boolean).join(', ')}</dd>
            </div>
            {ev.description ? (
              <div className="sm:col-span-2">
                <dt className="text-slate-600">{t('siteF.events.descriptionField')}</dt>
                <dd className="whitespace-pre-line">{ev.description}</dd>
              </div>
            ) : null}
          </dl>
          {canManage || deletable ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {canManage && ev.status === 'draft' ? (
                <Button variant="primary" disabled={setStatus.isPending} onClick={() => setStatus.mutate('published')}>
                  {t('siteF.events.publish')}
                </Button>
              ) : null}
              {canManage && ev.status === 'published' ? <ConfirmButton label={t('siteF.events.cancelEvent')} pending={setStatus.isPending} onConfirm={() => setStatus.mutate('cancelled')} /> : null}
              {deletable ? <ConfirmButton label={t('siteF.events.delete')} pending={remove.isPending} onConfirm={() => remove.mutate()} /> : null}
            </div>
          ) : null}
        </Card>
        <Card title={t('siteF.events.attendance')}>
          <p className="text-3xl font-semibold tabular-nums" aria-live="polite">
            {t('siteF.events.arrivedOf', { arrived: ev.counts.arrived, expected: ev.counts.expected })}
          </p>
          <p className="mt-1 text-sm text-slate-600">{t('siteF.events.walkInsLine', { n: ev.counts.walkIns })}</p>
          {ev.capacity !== null ? <p className="mt-1 text-sm text-slate-600">{t('siteF.events.capacityLine', { n: ev.capacity })}</p> : null}
          {over ? (
            <div className="mt-3">
              <Alert>{t('siteF.events.overCapacity')}</Alert>
            </div>
          ) : null}
        </Card>
      </div>

      <Card
        title={t('siteF.events.attendees')}
        description={t('siteF.events.attendeesHint')}
        className="mb-6"
      >
        <div className="space-y-4">
          {canManage ? (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setMode(mode === 'one' ? 'none' : 'one')}>{t('siteF.events.attendee.add')}</Button>
              <Button onClick={() => setMode(mode === 'bulk' ? 'none' : 'bulk')}>{t('siteF.events.bulk.open')}</Button>
            </div>
          ) : null}
          {mode === 'one' ? <AddOne eventId={ev.id} onDone={() => setMode('none')} /> : null}
          {mode === 'bulk' ? <BulkAdd eventId={ev.id} onDone={() => setMode('none')} /> : null}
          <TextField label={t('common.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} className="max-w-sm" />
          {attendees.isPending ? (
            <Spinner />
          ) : attendees.isError ? (
            <ErrorPanel error={attendees.error} onRetry={() => void attendees.refetch()} />
          ) : (
            <>
              <TableWrap caption={t('siteF.events.attendees')}>
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <Th>{t('siteF.events.attendee.name')}</Th>
                    <Th>{t('siteF.events.attendee.company')}</Th>
                    <Th>{t('siteF.events.attendee.email')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th>{t('common.actions')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {attendees.data.items.length === 0 ? <EmptyRow cols={5} /> : null}
                  {attendees.data.items.map((a) => (
                    <AttendeeRow key={a.id} attendee={a} canManage={canManage} />
                  ))}
                </tbody>
              </TableWrap>
              <Pager page={attendees.data.page} pageSize={attendees.data.pageSize} total={attendees.data.total} onPage={list.setPage} />
            </>
          )}
        </div>
      </Card>
    </>
  );
}
